import { useState, useMemo } from 'react';
import { Typography, Box, Tooltip } from '@mui/material';
import { DataGrid, GridActionsCellItem } from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import MainDialogBox from "../components/Dialog/MainDialogBox";
import DataGridSkeleton from "../components/DataGridSkeleton";
import CustomPagination from "./CustomPagination";
import useClusterZoneStore from '../../store/useClusterZoneStore';
import { useApartmentByCluster } from '../../assets/api/useAdminApi';
import { useDialog } from '../hook/useDialog';
import CustomDivider from './CustomDivider';
import useAuthStore from '../../store/useAuthStore';

export const useClusterColumnsWithDialog = (isEdit = true) => {
    const { user } = useAuthStore()
    const [selectedRow, setSelectedRow] = useState({});
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 5 });

    const { open, handleClickOpen, handleClose } = useDialog();
    const { setClusterZone } = useClusterZoneStore();

    const { data: apartmentList, isLoading: isApartmentLoading } = useApartmentByCluster({
        cluster_id: selectedRow?.id,
    });

    const apartmentRows = useMemo(() => {
        return apartmentList?.map((item, index) => ({
            ...item,
            sr_no: index + 1,
        })) || [];
    }, [apartmentList]);

    const viewCluster = (row) => {
        setSelectedRow(row);
        handleClickOpen();
    };

    const clusterColumns = [
        {
            field: 'sr_no',
            headerName: 'Sr No',
            flex: 1,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: 'cluster_name',
            headerName: 'Cluster Name',
            flex: 1,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: 'zone_name',
            headerName: 'Zone Name',
            flex: 1,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: 'country_name',
            headerName: 'Country Name',
            flex: 1,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: 'state_name',
            headerName: 'State Name',
            flex: 1,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: 'city_name',
            headerName: 'City Name',
            flex: 1,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        ...((isEdit && user?.role !== "Zonal Coordinator")
            ? [
                {
                    field: 'actions',
                    type: 'actions',
                    headerName: 'Actions',
                    flex: 1,
                    cellClassName: 'actions',
                    renderCell: (params) => (
                        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                            <Tooltip title="Edit">
                                <GridActionsCellItem
                                    icon={<EditIcon />}
                                    label="Edit"
                                    onClick={() => {
                                        setClusterZone(params?.row);
                                        window.scrollTo({ top: 0, behavior: 'smooth' });
                                    }}
                                />
                            </Tooltip>
                        </Box>
                    ),
                },
            ]
            : []),
    ];

    const apartmentColumns = [
        {
            field: 'sr_no',
            headerName: 'Sr No',
            flex: 1,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: 'apartment_name',
            headerName: 'Apartment Name',
            flex: 1,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: 'apartment_status',
            headerName: 'Apartment Status',
            flex: 1,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
        {
            field: 'remarks',
            headerName: 'Remarks',
            flex: 1,
            renderCell: (params) => <Typography variant="body2">{params.value}</Typography>,
        },
    ];

    const dialogJSX = (
        <MainDialogBox open={open} handleClose={handleClose} width="60%">
            <Typography variant='h4'>Aparment List</Typography>
            <CustomDivider />
            {isApartmentLoading ? (
                <DataGridSkeleton rowsNum={5} />
            ) : (
                <DataGrid
                    rows={apartmentRows}
                    columns={apartmentColumns}
                    getRowId={(row) => row.id}
                    pagination
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[5]}
                    slots={{
                        pagination: () => (
                            <CustomPagination
                                paginationModel={paginationModel}
                                setPaginationModel={setPaginationModel}
                                totalRows={apartmentRows?.length || 0}
                                pageSize={paginationModel.pageSize}
                            />
                        ),
                    }}
                />
            )}
        </MainDialogBox>
    );

    return { clusterColumns, viewCluster, dialogJSX };
};
